/** URL 工具函数集合 */
import { isServer } from "solid-js/web";

import { getLocale } from "@/i18n";
import { DefaultLocale, Locale, LocaleMap } from "@/i18n/config";

import {
  getLocaleFromPath,
  getLocalizedPath,
  updateUrlLocale,
} from "./language.ts";

/**
 * 获取 URL 中的查询参数
 *
 * @param name 参数名
 * @param url 可选，默认使用当前页面 URL
 * @returns 参数值
 */
export const getQueryParam = (name: string, url?: string): string | null => {
  if (!isServer) {
    const searchParams = new URLSearchParams(
      url ? new URL(url).search : window.location.search,
    );
    return searchParams.get(name);
  }
  return null;
};

/**
 * 获取所有 URL 查询参数
 *
 * @param url 可选，默认使用当前页面 URL
 * @returns 参数对象
 */
export const getAllQueryParams = (url?: string): Record<string, string> => {
  const searchParams = new URLSearchParams(
    url ? new URL(url).search : window.location.search,
  );
  return Object.fromEntries(searchParams.entries()) as Record<string, string>;
};

/**
 * 构建带查询参数的 URL
 *
 * @param baseUrl 基础 URL
 * @param params 参数对象
 * @returns 完整的 URL
 */
export const buildUrl = (
  baseUrl: string,
  params: Record<string, string>,
): string => {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });
  return url.toString();
};

/**
 * 更新 URL 中的查询参数
 *
 * @param params 要更新的参数对象
 * @param replace 是否替换历史记录
 */
export const updateQueryParams = (
  params: Record<string, string>,
  replace: boolean = false,
): void => {
  const url = new URL(window.location.href);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  });

  const newUrl = url.toString();
  if (replace) {
    window.history.replaceState({}, "", newUrl);
  } else {
    console.log(`updateQueryParams path: ${newUrl}`);
    console.log(
      `updateQueryParams window.location.href: ${window.location.href}`,
    );
    window.history.pushState({}, "", newUrl);
  }
};

/**
 * 去除路径中的语言前缀（如 /en、/zh）
 *
 * @param path 含语言前缀的完整路径
 * @returns 去掉语言前缀的路径（如 "/casino"）
 */
export function removeLocalePrefix(path: string): string {
  console.log(`removeLocalePrefix path: ${path}`);
  const normalizedPath = normalizePath(path); // 确保路径格式一致，如去除重复斜杠
  const segments = normalizedPath.split("/").filter(Boolean);

  // 如果首段是语言标识，则移除
  if (segments.length > 0 && Object.keys(LocaleMap).includes(segments[0])) {
    segments.shift();
  }
  console.log(`removeLocalePrefix segments: ${"/" + segments.join("/")}`);

  return "/" + segments.join("/");
}

/** 处理斜杠 */
export function normalizePath(path: string): string {
  if (path === "/") return "";
  // 去除末尾斜杠（除非是根路径）
  const trimmed = path.endsWith("/") && path !== "/" ? path.slice(0, -1) : path;
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

/**
 * 对象转换为 URL 参数字符串
 *
 * @param obj 参数对象
 * @returns 参数字符串(user=jack,userId=12953093)
 */
export function objToUrlParams(obj: Record<string, string>) {
  const str = Object.entries(obj)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
  return str;
}

// 重新导出语言相关的函数
export { getLocaleFromPath, getLocalizedPath, updateUrlLocale };
